/**
 * Trace a number: from one cell of a table back to what made it, read off the session's lineage
 * graph. Pure: the graph, the table's name and the cell's two leaves come in; the tree the trace
 * panel shows comes out.
 *
 * The table is named by its label and its edge reads the rows and columns (operands), the base
 * (universe) and the weight in use. Each of those is traced back through every edge that wrote
 * it, in the order they ran (a FIELD creates, an OR or ASSIGN changes), and through the edges of
 * the variables under it (a QTABLE is filled by APPENDs into it and into its groups), down to the
 * collected variables, which no edge writes. A variable reached again while it is being traced is
 * a cycle and stops there.
 */

import { LineageDag, LineageEdge, LineageSourceRole } from "types/lineage.types"
import type { AxisLeaf } from "utils/tableModel"
import { displayLabel, normLabel } from "./lineageLabels"
import { edgeCallOf, SessionCall } from "./lineageCalls"

/** The cell to trace: the engine table it came from and its two leaves. */
export interface TraceRequest {
  /** The engine table the cell's number came from. */
  table: string
  down: AxisLeaf
  across: AxisLeaf
  /** The number as the table shows it. */
  value?: string
  /** The table as the screen calls it. */
  title?: string
  /** The script that produced the table shown (see `TraceOptions.tableScript`). */
  tableScript?: string
  /** The table was output transposed: its rows run across. */
  transposed?: boolean
  /** Said with the cell: which part of the table the trace follows, when it is not the whole. */
  note?: string
}

/** A variable as the trace shows it: how it was made, one step per edge that wrote it. */
export interface TraceVariable {
  id: string
  /** What the panel calls it: a helper's description, else its name without the engine's brackets. */
  label: string
  /** Whether a helper the client's compilers named (`describeHelper` knew it). */
  helper: boolean
  /** No edge writes the variable itself: it came with the data. */
  collected: boolean
  /** The edges that wrote it or a variable under it, in the order they ran. */
  steps: TraceStep[]
  /** Reached again while it was being traced: its steps are shown where it first appears. */
  cycle?: boolean
}

export interface TraceSource {
  role: LineageSourceRole
  variable: TraceVariable
}

/**
 * One edge: `command` wrote `target` reading `sources`, in call `script` of the session (a position
 * in `sessionCalls`), as entry `entry` of the command log (schema 4).
 */
export interface TraceStep {
  target: string
  targetLabel: string
  command: string
  script?: number
  entry?: number
  sources: TraceSource[]
}

/**
 * The category a row or column of the cell stands for.
 * - "derived": a category the session made (a net, a recode), with its steps;
 * - "answer": an answer of a collected question, `answer` under `question`;
 * - "total": the Totals, everyone in the table's base;
 * - "whole": not one category the graph or the data has (a net of the table, a statistic, a
 *   label that could not be matched): the whole variables of that side stand for it.
 */
export type TraceCategory =
  | { kind: "derived"; variable: TraceVariable }
  | { kind: "answer"; question: TraceVariable; answer: string }
  | { kind: "total" }
  | { kind: "whole"; variables: TraceVariable[] }

/**
 * What one side of the cell reads: the node of its category (a category made in the session, or
 * the question of an answer), nothing for the Totals, or the whole variables of the side when the
 * category is no node the graph has (`whole`).
 */
export interface TraceSide {
  variables: TraceVariable[]
  whole: boolean
}

export interface TableTrace {
  /** The table's node in the graph. */
  table: string
  command: string
  /** The call of the session that recorded the table's edge (a position in `sessionCalls`). */
  script?: number
  /**
   * Whether the edge is known to be the definition shown: it matches the table's script, or no
   * script was given. False when the table has edges but none matches the script given: the last
   * one is shown.
   */
  confirmed: boolean
  rows: TraceVariable[]
  columns: TraceVariable[]
  base: TraceVariable[]
  weight: TraceVariable[]
  /** Sources the table line does not account for. */
  other: TraceSource[]
  rowCategory: TraceCategory
  columnCategory: TraceCategory
  /** What the cell reads of the rows: its own category, not every category of the rows. */
  rowReads: TraceSide
  /** What the cell reads of the columns, the same way. */
  columnReads: TraceSide
  /** Every call that made a step of the trace, in the order the trace first meets them. */
  scripts: number[]
}

export interface TraceOptions {
  /**
   * The script that produced the table shown. Its XTAB or DTAB line picks the table's edge (the
   * engine records the same definition once, so a table changed and changed back has its current
   * definition earlier in the graph) and says which operands are rows and which are columns.
   */
  tableScript?: string
  /** The table was output transposed (/T): its rows run across. */
  transposed?: boolean
  /** The description of a helper name the client's compilers made, by its first segment. */
  describeHelper?: (name: string) => string | undefined
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** An engine identifier's labels: "<<A b>>::C" is ["A b", "C"]. */
export const idSegments = (id: string): string[] => {
  const segments: string[] = []
  let current = ""
  let i = 0
  while (i < id.length) {
    if (id.startsWith("<<", i)) {
      const end = id.indexOf(">>", i + 2)
      if (end < 0) {
        current += id.slice(i + 2)
        break
      }
      current += id.slice(i + 2, end)
      i = end + 2
    } else if (id.startsWith("::", i)) {
      segments.push(current)
      current = ""
      i += 2
    } else {
      current += id[i]
      i += 1
    }
  }
  segments.push(current)
  return segments.map((s) => displayLabel(s))
}

/** How the panel names `id`: a helper by its description, then the labels under it. */
export const variableLabel = (
  id: string,
  describeHelper?: (name: string) => string | undefined
): { label: string; helper: boolean } => {
  const segments = idSegments(id)
  const described = describeHelper?.(segments[0])
  if (described) return { label: [described, ...segments.slice(1)].join(" › "), helper: true }
  return { label: segments.join(" › "), helper: false }
}

// ---------------------------------------------------------------------------
// The table's script line
// ---------------------------------------------------------------------------

interface TableLine {
  command: string
  rows: string[]
  columns: string[]
  universe: string | null
  /** The weight the script set before the line: null for none, undefined when it sets none. */
  weight: string | null | undefined
}

const TOKEN = /<<.*?>>|"[^"]*"|\S+/g
const KEYWORD = /^[A-Za-z]+:/

/**
 * The last XTAB or DTAB line of `script` that writes `table`, with its rows and columns (each a
 * list: `|` combines), its universe and the weight the script set before it.
 */
export const readTableLine = (script: string, table: string): TableLine | null => {
  const want = normLabel(table)
  let found: TableLine | null = null
  let weight: string | null | undefined
  for (const line of script.split(/\r?\n/)) {
    const weights = /^\s*USEWEIGHTS\b\s*(.*)$/i.exec(line)
    if (weights) {
      weight = weights[1].trim() || null
      continue
    }
    const m = /^\s*(XTAB|DTAB)\s+(\S+)(.*)$/i.exec(line)
    if (!m || normLabel(m[2]) !== want) continue
    const tokens = m[3].match(TOKEN) ?? []
    const groups: string[][] = []
    let universe: string | null = null
    let combine = false
    // Rows and columns come first; after the first option only UNIVERSE: is read.
    let inOptions = false
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]
      if (/^UNIVERSE:/i.test(token)) {
        const rest = token.slice("UNIVERSE:".length)
        if (rest) universe = rest
        else {
          universe = tokens[i + 1] ?? null
          i += 1
        }
        inOptions = true
        continue
      }
      if (KEYWORD.test(token)) inOptions = true
      if (inOptions) continue
      if (token === "|") {
        combine = true
        continue
      }
      if (combine && groups.length > 0) groups[groups.length - 1].push(token)
      else groups.push([token])
      combine = false
    }
    found = {
      command: m[1].toUpperCase(),
      rows: groups[0] ?? [],
      columns: groups.slice(1).flat(),
      universe,
      weight,
    }
  }
  return found
}

// ---------------------------------------------------------------------------
// The trace
// ---------------------------------------------------------------------------

const COPYING = new Set(["APPEND", "APPENDSTRUCT", "COPY", "COPYSTRUCT"])

const roleOf = (edge: LineageEdge, i: number): LineageSourceRole => edge.sourceRoles?.[i] ?? "operand"

/** Whether `edge`'s sources match the table line (the weight only when the script set one). */
const matchesLine = (edge: LineageEdge, line: TableLine): boolean => {
  if (edge.command.toUpperCase() !== line.command) return false
  const want = new Set([...line.rows, ...line.columns].map(normLabel))
  if (line.universe) want.add(normLabel(line.universe))
  if (line.weight) want.add(normLabel(line.weight))
  const have = new Set(
    edge.sources
      .filter((_, i) => line.weight !== undefined || roleOf(edge, i) !== "weight")
      .map(normLabel)
  )
  if (have.size !== want.size) return false
  for (const s of have) if (!want.has(s)) return false
  return true
}

/** What a side of the cell reads, by the category it stands for. */
const sideOf = (category: TraceCategory): TraceSide => {
  switch (category.kind) {
    case "derived":
      return { variables: [category.variable], whole: false }
    case "answer":
      return { variables: [category.question], whole: false }
    case "total":
      return { variables: [], whole: false }
    case "whole":
      return { variables: category.variables, whole: true }
  }
}

/**
 * The trace of the cell (`down`, `across`) of table `tableName`, or null when the graph has no
 * edge for the table (the session was reset since the table was shown, or it never ran).
 */
export const traceTable = (
  dag: LineageDag,
  tableName: string,
  down: AxisLeaf | null,
  across: AxisLeaf | null,
  options: TraceOptions = {}
): TableTrace | null => {
  const wantTable = normLabel(tableName)
  const tableEdges = dag.edges.filter((e) => normLabel(e.target) === wantTable)
  if (tableEdges.length === 0) return null

  const line = options.tableScript ? readTableLine(options.tableScript, tableName) : null
  const matched = line ? [...tableEdges].reverse().find((e) => matchesLine(e, line)) : undefined
  const edge = matched ?? tableEdges[tableEdges.length - 1]
  const confirmed = !options.tableScript || matched !== undefined

  // Every name the graph knows, by its matching form.
  const known = new Map<string, string>()
  dag.nodes.forEach((n) => known.set(normLabel(n.id), n.id))
  dag.edges.forEach((e) => {
    known.set(normLabel(e.target), e.target)
    e.sources.forEach((s) => {
      if (!known.has(normLabel(s))) known.set(normLabel(s), s)
    })
  })
  const normTargets = dag.edges.map((e) => normLabel(e.target))
  const normWritten = new Set(normTargets)
  /**
   * Whether an edge writes `id` or a variable above it: an ASSIGN into a field changes every
   * category under it, and a QTABLE or APPENDSTRUCT above it made it.
   */
  const writtenAt = (id: string): boolean => {
    const parts = normLabel(id).split("::")
    for (let k = parts.length; k >= 1; k--) if (normWritten.has(parts.slice(0, k).join("::"))) return true
    return false
  }

  const callOf = edgeCallOf(dag)
  const scripts: number[] = []
  const noteScript = (index: number | undefined): void => {
    if (index !== undefined && !scripts.includes(index)) scripts.push(index)
  }
  noteScript(callOf(edge))

  const traced = new Map<string, TraceVariable>()
  const inProgress = new Set<string>()

  const trace = (id: string): TraceVariable => {
    const done = traced.get(id)
    if (done) return done
    const { label, helper } = variableLabel(id, options.describeHelper)
    if (inProgress.has(id)) return { id, label, helper, collected: false, steps: [], cycle: true }
    inProgress.add(id)
    const norm = normLabel(id)
    const steps: TraceStep[] = []
    dag.edges.forEach((e, i) => {
      const t = normTargets[i]
      // The variable itself, a variable under it, or one above it that read something (see
      // writtenAt; a QTABLE above it is only the container).
      const above = norm.startsWith(`${t}::`)
      if (t !== norm && !t.startsWith(`${norm}::`) && !(above && e.sources.length > 0)) return
      // A table is defined by its last edge, the one picked above for the traced table.
      if (e !== edge && t === wantTable) return
      noteScript(callOf(e))
      steps.push({
        target: e.target,
        targetLabel: variableLabel(e.target, options.describeHelper).label,
        command: e.command,
        script: callOf(e),
        entry: e.entry,
        sources: e.sources.map((s, k) => ({ role: roleOf(e, k), variable: trace(s) })),
      })
    })
    inProgress.delete(id)
    const variable: TraceVariable = { id, label, helper, collected: !writtenAt(id), steps }
    traced.set(id, variable)
    return variable
  }

  // The table's sources by what the table line made of them: rows first, then columns.
  const rows: TraceVariable[] = []
  const columns: TraceVariable[] = []
  const base: TraceVariable[] = []
  const weight: TraceVariable[] = []
  const other: TraceSource[] = []
  const rowNames = new Set((line?.rows ?? []).map(normLabel))
  const columnNames = new Set((line?.columns ?? []).map(normLabel))
  // Without the line, the first operand is the rows (both commands read their rows first).
  const rowCount = line ? line.rows.length : 1
  let operand = 0
  edge.sources.forEach((s, i) => {
    const role = roleOf(edge, i)
    if (role === "universe") base.push(trace(s))
    else if (role === "weight") weight.push(trace(s))
    else if (role === "operand") {
      const n = normLabel(s)
      if (rowNames.has(n)) rows.push(trace(s))
      else if (columnNames.has(n)) columns.push(trace(s))
      else if (operand < rowCount) rows.push(trace(s))
      else columns.push(trace(s))
      operand += 1
    } else other.push({ role, variable: trace(s) })
  })

  /**
   * The node for `rest` under `id`: the node itself, a node on the way down, or through an
   * APPEND or COPY into it the variable it was copied from. An answer of a collected variable is
   * no node of its own.
   */
  const resolveIn = (
    id: string,
    rest: string[],
    seen: Set<string>
  ): { node: string } | { question: string; answer: string } | null => {
    if (rest.length === 0) return { node: id }
    if (seen.has(id)) return null
    seen.add(id)
    const norm = normLabel(id)
    for (let k = rest.length; k >= 1; k--) {
      const key = [norm, ...rest.slice(0, k).map(normLabel)].join("::")
      const hit = known.get(key)
      if (hit) return resolveIn(hit, rest.slice(k), seen)
    }
    // Copied in: the leaf can stand under the copied variable's own name, or straight under it.
    for (let i = dag.edges.length - 1; i >= 0; i--) {
      const e = dag.edges[i]
      if (normTargets[i] !== norm || !COPYING.has(e.command.toUpperCase())) continue
      for (const s of e.sources) {
        const segments = idSegments(s)
        const last = segments[segments.length - 1]
        if (rest.length > 1 && normLabel(last) === normLabel(rest[0])) {
          const r = resolveIn(s, rest.slice(1), seen)
          if (r) return r
        }
        const r = resolveIn(s, rest, seen)
        if (r) return r
      }
    }
    if (!writtenAt(id) && rest.length === 1) return { question: id, answer: rest[0] }
    return null
  }

  const categoryOf = (leaf: AxisLeaf | null, mine: TraceVariable[], others: TraceVariable[]): TraceCategory => {
    if (!leaf || leaf.isTotal) return { kind: "total" }
    const path = leaf.path.filter((p) => p !== "")
    if (path.length === 0) return { kind: "total" }
    // The leaf's own side first; a transposed or reshaped table can hold it on the other, where
    // only a node the graph has counts (any label would pass for an answer of a collected one).
    for (const side of [mine, others]) {
      for (let skip = 0; skip < path.length; skip++) {
        for (const v of side) {
          const found = resolveIn(v.id, path.slice(skip), new Set())
          if (!found) continue
          if ("question" in found && side !== mine) continue
          if ("question" in found) return { kind: "answer", question: trace(found.question), answer: found.answer }
          if (writtenAt(found.node)) return { kind: "derived", variable: trace(found.node) }
          // A collected category: the answer it is, under its question.
          const segments = idSegments(found.node)
          if (segments.length > 1) {
            const parentNorm = normLabel(found.node).split("::").slice(0, -1).join("::")
            const parent = known.get(parentNorm)
            if (parent)
              return { kind: "answer", question: trace(parent), answer: segments[segments.length - 1] }
          }
          return { kind: "derived", variable: trace(found.node) }
        }
      }
    }
    return { kind: "whole", variables: mine.length > 0 ? mine : others }
  }

  const transposed = options.transposed === true
  const downVars = transposed ? columns : rows
  const acrossVars = transposed ? rows : columns
  const downCategory = categoryOf(down, downVars, acrossVars)
  const acrossCategory = categoryOf(across, acrossVars, downVars)
  const rowCategory = transposed ? acrossCategory : downCategory
  const columnCategory = transposed ? downCategory : acrossCategory

  return {
    table: edge.target,
    command: edge.command,
    script: callOf(edge),
    confirmed,
    rows,
    columns,
    base,
    weight,
    other,
    rowCategory,
    columnCategory,
    rowReads: sideOf(rowCategory),
    columnReads: sideOf(columnCategory),
    scripts,
  }
}

// ---------------------------------------------------------------------------
// The scripts
// ---------------------------------------------------------------------------

/** One part of a preparation as it was sent: what it is called, and its script. */
export interface PreparationScript {
  label: string
  script: string
}

export interface ScriptLabelOptions {
  /** The call that recorded the traced table's edge (a position in `sessionCalls`). */
  tableScript?: number
  /** What the session's preparation sent, part by part. */
  preparation?: PreparationScript[]
  /** A feature's own name for a script, from its text (another tile's or table's script). */
  describe?: (script: string) => string | undefined
}

/** The name of the table a trace runs to work out a cell's numbers (see `traceCheck`). */
export const TRACE_CHECK = "__trace_check"

/**
 * The graph without the trace checks: a check is removed as soon as it has run and only the trace
 * panel reads it, so the graph leaves it out. The script log still lists the check.
 */
export const withoutTraceChecks = (dag: LineageDag): LineageDag => {
  const isCheck = (id: string): boolean => normLabel(id) === TRACE_CHECK
  if (!dag.nodes.some((n) => isCheck(n.id))) return dag
  return { ...dag, nodes: dag.nodes.filter((n) => !isCheck(n.id)), edges: dag.edges.filter((e) => !isCheck(e.target)) }
}
const WRITES_TRACE_CHECK = /^\s*(?:XTAB|DTAB)\s+__trace_check\b/im

const isReset = (c: SessionCall): boolean => c.kind === "run" && c.text.trim().toUpperCase() === "RESET"

/** A script's lines, trimmed and without blank ones. */
const linesOf = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== "")

/** The commands the engine does not log (they only read), so a call's commands never hold them. */
const OBSERVE_ONLY = new Set([
  "DUMP",
  "SDUMP",
  "CODEBOOK",
  "PDUMP",
  "OUTPUT",
  "OUTPUTJ",
  "FIND",
  "FINDR",
  "INFO",
  "HELP",
  "TEST",
  "LINEAGE",
])

/**
 * A script as the engine's command log reads it: one command per line with single spaces between
 * its params, without comments, blank lines and the commands that only read. A script as it was
 * sent and a call of the command log (schema 4) compare equal this way, unless it used FOR or
 * DEFINE (the log holds them expanded).
 */
export const loggedForm = (text: string): string =>
  linesOf(text)
    .filter((l) => !l.startsWith("#"))
    .map((l) => (l.match(TOKEN) ?? []).join(" "))
    .filter((l) => !OBSERVE_ONLY.has(l.split(" ")[0].toUpperCase()))
    .join("\n")

/** The part of the preparation that sent `text` (a script as it was sent, or a call's commands). */
export const preparationPart = (
  text: string,
  preparation: PreparationScript[] | undefined
): PreparationScript | undefined => {
  if (!preparation) return undefined
  const want = loggedForm(text)
  return (
    preparation.find((p) => p.script.trim() === text.trim()) ??
    preparation.find((p) => loggedForm(p.script) === want)
  )
}

/** The lines the Conductor sends after the Run on load scripts. */
const ON_LOAD_CLOSING = ["EXCL", "SCOPE", "USEWEIGHTS", "SETSTART"]

/** What one of the lines that close a preparation does, or undefined for any other script. */
const closingLine = (text: string): string | undefined => {
  const lines = linesOf(text)
  if (lines.length !== 1) return undefined
  const [command, ...rest] = lines[0].split(/\s+/)
  const word = command.toUpperCase()
  if (rest.length === 0 && word === "EXCL") return "clear the exclusions"
  if (rest.length === 0 && word === "SCOPE") return "clear the scope"
  if (word === "USEWEIGHTS") return rest.length === 0 ? "no weight" : `weight ${displayLabel(rest.join(" "))}`
  return undefined
}

/**
 * What the trace and the script log call call `index` of the session (see `sessionCalls`: a call of
 * the command log, or a script of a schema 3 script log). A call reads as its commands, one per
 * line:
 * - "This table's script": the one that recorded the table's edge;
 * - "Data loaded";
 * - "Run on load": a script the Conductor ran into the instance before the session's first RESET
 *   (the dashboards and tab specs reset before they run anything; RESET returns to the data with
 *   the Run on load scripts applied), and the closing lines it sends after them;
 * - "Back to the start point": a RESET;
 * - "Preparation: ..." by what a closing line (bare EXCL, bare SCOPE, USEWEIGHTS) does, or for
 *   another part of the preparation by its label;
 * - "Trace check": the check a trace ran to work out a cell's numbers (it writes `TRACE_CHECK`);
 * - a feature's own name, else "Script n" (counted from 1).
 */
export const scriptLabel = (
  calls: SessionCall[],
  index: number,
  options: ScriptLabelOptions = {}
): string => {
  if (index === options.tableScript) return "This table's script"
  const entry = calls[index]
  if (!entry) return `Script ${index + 1}`
  if (entry.kind === "create") return "Data loaded"
  const text = entry.text.trim()
  const firstReset = calls.findIndex(isReset)
  if (firstReset > index) {
    const closing = linesOf(text).map((l) => l.toUpperCase()).join("\n") === ON_LOAD_CLOSING.join("\n")
    return closing ? "Run on load: clear exclusions, scope and weight, and set the start point" : "Run on load"
  }
  if (isReset(entry)) return "Back to the start point (RESET)"
  if (WRITES_TRACE_CHECK.test(text)) return "Trace check"
  const closing = closingLine(text)
  if (closing) return `Preparation: ${closing}`
  const part = preparationPart(text, options.preparation)
  if (part) return `Preparation: ${part.label}`
  return options.describe?.(text) ?? `Script ${index + 1}`
}
